import type {
  AssessmentStatus,
  ItemEvaluation,
  ProcessPriority,
  ProcessType,
  ContractStatus,
  SupplierStatus,
  EvidenceStatus,
  RiskLevel,
  ContractState,
  TierVariant,
  NotificationHistoryStatus
} from '@app-types';

/**
 * Badge variant types for visual representation
 */
export type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'purple' | 'orange' | 'pending' | 'default';

/**
 * Maps AssessmentStatus to Badge variant
 * Centralizes the color logic for assessment statuses
 */
export const statusToVariant = (status: AssessmentStatus): BadgeVariant => {
  const mapping: Record<AssessmentStatus, BadgeVariant> = {
    Final: 'success',
    'Pending Evidence': 'purple',
    'Evidence Past Due': 'warning',
    Escalated: 'error',
    Scheduled: 'info',
    Rescheduled: 'orange',
    Pending: 'pending',
    Cancelled: 'error',
    Expired: 'default',
    'Not Scheduled': 'default'
  };
  return mapping[status] || 'default';
};

/**
 * Maps a notification-history delivery status to a Badge variant
 * Centralizes the color logic for the admin Notification History tab
 */
export const notificationHistoryStatusToVariant = (status: NotificationHistoryStatus): BadgeVariant => {
  const mapping: Record<NotificationHistoryStatus, BadgeVariant> = {
    sent: 'success',
    failed: 'error',
    pending: 'pending',
    skipped: 'default'
  };
  return mapping[status] || 'default';
};

/**
 * Maps ItemEvaluation to Badge variant
 * Centralizes the color logic for item evaluations
 */
export const evaluationToVariant = (evaluation: ItemEvaluation): BadgeVariant => {
  const mapping: Record<ItemEvaluation, BadgeVariant> = {
    Implemented: 'success',
    'Implemented (Needs Evidence)': 'warning',
    Gap: 'error',
    'Not Applicable': 'info',
    'Not Assessed': 'default',
    Remediated: 'success'
  };
  return mapping[evaluation] || 'default';
};

/**
 * Maps ProcessPriority to Badge variant
 * Centralizes the color logic for process priorities
 */
export const priorityToVariant = (priority: ProcessPriority): BadgeVariant => {
  const mapping: Record<ProcessPriority, BadgeVariant> = {
    High: 'error',
    Medium: 'warning',
    Low: 'info'
  };
  return mapping[priority] || 'default';
};

/**
 * Maps ProcessType to Badge variant
 * Purple/gray keep ENT and OPS apart from the red/amber/blue priority pill shown next to them
 */
export const processTypeToVariant = (processType: ProcessType): BadgeVariant => {
  const mapping: Record<ProcessType, BadgeVariant> = {
    ENT: 'purple',
    OPS: 'default'
  };
  return mapping[processType];
};

/**
 * Maps Contract Status to Badge variant
 * Centralizes the color logic for contract statuses
 */
export const contractStatusToVariant = (status: ContractStatus): BadgeVariant => {
  const mapping: Record<ContractStatus, BadgeVariant> = {
    Active: 'success',
    Inactive: 'default',
  };
  return mapping[status] || 'default';
};

/**
 * Maps Evidence Status to Badge variant
 * Centralizes the color logic for evidence statuses
 */
export const evidenceStatusToVariant = (status: EvidenceStatus): BadgeVariant => {
  const mapping: Record<EvidenceStatus, BadgeVariant> = {
    approved: 'success',
    draft: 'info',
    pending: 'warning',
    rejected: 'error',
    mixed: 'warning',
    none: 'default'
  };
  return mapping[status];
};

/**
 * Maps Risk Level to Badge variant
 * Centralizes the color logic for risk levels
 */
export const riskLevelToVariant = (riskLevel: RiskLevel): BadgeVariant => {
  const mapping: Record<RiskLevel, BadgeVariant> = {
    High: 'error',
    Medium: 'warning',
    Low: 'success',
    Unknown: 'default'
  };
  return mapping[riskLevel];
};

/**
 * Maps Supplier Status to Badge variant
 * Centralizes the color logic for supplier statuses
 */
export const supplierStatusToVariant = (status: SupplierStatus): BadgeVariant => {
  const mapping: Record<SupplierStatus, BadgeVariant> = {
    Active: 'success',
    Inactive: 'default',
    Deleted: 'error'
  };
  return mapping[status];
};

/**
 * Maps Contract State to Badge variant
 * Centralizes the color logic for contract scheduling states
 */
export const contractStateToVariant = (state: ContractState): BadgeVariant => {
  const normalized = state.toLowerCase().replace(/_/g, ' ');

  const mapping: Record<string, BadgeVariant> = {
    'pending data enrichment': 'warning',
    'ready for scheduling': 'info',
    scheduled: 'info',
    conflicts: 'error',
    'not scheduled': 'default',
    'in progress': 'warning',
    completed: 'success'
  };

  return mapping[normalized] || 'default';
};

/**
 * Maps Tier name to TierBadge variant
 * Centralizes the color logic for supplier tiers
 */
export const tierToVariant = (tierName: string): TierVariant => {
  if (!tierName) return 'default';

  const tierLower = tierName.toLowerCase();
  const tierMatch = tierLower.match(/tier\s*(\d+)/);

  if (tierMatch) {
    const tierNumber = Number.parseInt(tierMatch[1], 10);
    switch (tierNumber) {
      case 1:
        return 'tier1';
      case 2:
        return 'tier2';
      case 3:
        return 'tier3';
      case 4:
        return 'tier4';
      default:
        return 'default';
    }
  }

  return 'default';
};
